import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Logo } from "@/components/Logo";
import { AppFooter } from "@/components/AppFooter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PasswordInput } from "@/components/PasswordInput";
import { api, formatApiError, setAuthToken } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { KeyRound, ShieldAlert } from "lucide-react";

export default function ResetPassword() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get(`/auth/patient/reset-password/${token}`)
      .then(({ data }) => setInfo(data))
      .catch((e) => setError(formatApiError(e)));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (password !== confirm) { toast.error("Passwords don't match"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/patient/reset-password", { token, password });
      if (data.access_token) setAuthToken(data.access_token);
      setUser(data.user);
      toast.success("Password reset successfully");
      navigate("/me/today", { replace: true });
    } catch (err) { toast.error(formatApiError(err)); } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen flex flex-col bg-background" data-testid="reset-password-page">
      <main className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="max-w-md w-full">
          <Logo size="lg" />
          <h1 className="text-3xl font-bold tracking-tight mt-10">Reset password</h1>

          {error ? (
            <div className="mt-6 p-5 rounded-lg bg-destructive/10 text-destructive flex gap-3" data-testid="reset-error">
              <ShieldAlert size={18} className="mt-0.5" />
              <div>
                <div className="font-semibold">Link invalid or expired</div>
                <p className="text-sm mt-1">{error}</p>
                <Button variant="outline" onClick={() => navigate("/login")} className="mt-3">Back to login</Button>
              </div>
            </div>
          ) : !info ? (
            <p className="mt-6 text-muted-foreground">Validating link…</p>
          ) : (
            <form onSubmit={submit} className="mt-8 space-y-5" data-testid="reset-password-form">
              <div className="rounded-lg bg-secondary text-secondary-foreground px-4 py-3 text-sm flex gap-3">
                <KeyRound size={18} className="mt-0.5" />
                <div>
                  <div className="font-semibold">Account <span>{info.email || info.email_hint}</span></div>
                  <div className="text-xs">Reset link for {info.email_hint}</div>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-pwd">New password</Label>
                <PasswordInput id="new-pwd" required minLength={6} value={password} onChange={(e) => setPassword(e.target.value)} className="h-12" testId="reset-new-password" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-pwd">Confirm password</Label>
                <PasswordInput id="confirm-pwd" required value={confirm} onChange={(e) => setConfirm(e.target.value)} className="h-12" testId="reset-confirm-password" />
              </div>
              <Button type="submit" disabled={submitting} className="w-full h-12 rounded-full font-semibold" data-testid="reset-submit-button">
                {submitting ? "Updating…" : "Reset password & sign in"}
              </Button>
            </form>
          )}
        </div>
      </main>
      <AppFooter />
    </div>
  );
}
